export interface ModerationRequest {
    id: number;
    companyBin: string;
    companyName: string;
    contactPerson: ContactPerson;
    status: UserStatus;
    submittedAt: string;
    waitingTimeHours: number;
    moderatorComment?: string;
    documentsCount: number;
}

export interface ContactPerson {
    fullName: string;
    phone: string;
    email: string;
}

export interface ClientModerationDetails {
    id: number;
    user: User;
    company: Company;
    status: UserStatus;
    documents: Document[];
    auditHistory: AuditLog[];
    comments: Comment[];
}

export interface AuditLog {
    id: number;
    entityType: string;
    entityId: number;
    action: string;
    moderatorId?: number;
    details: string;
    timestamp: string;
    ipAddress?: string;
}

export interface Comment {
    id: number;
    content: string;
    moderatorName: string;
    createdAt: string;
}

export interface ModerationFilters {
    status: string;
    searchTerm: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
}

export interface ApprovalRequest {
    comment?: string;
    moderatorId: string;
}

export interface RejectionRequest {
    reason: string;
    additionalComment?: string;
    moderatorId: string;
}

export interface ChangeRequest {
    changes: string[];
    additionalComment?: string;
    moderatorId: string;
}

export interface ModerationStats {
    totalApplications: number;
    approvedApplications: number;
    rejectedApplications: number;
    avgProcessingTime: number;
    chartData: ChartData[];
    moderatorActivity: ModeratorActivity[];
}

export interface ChartData {
    date: string;
    approved: number;
    rejected: number;
    pending: number;
}

export interface ModeratorActivity {
    moderatorId: number;
    moderatorName: string;
    approvedCount: number;
    rejectedCount: number;
    avgProcessingTime: number;
}

export interface Contract {
    id: number;
    companyId: number;
    companyName: string;
    status: ContractStatus;
    createdAt: string;
    documentUrl: string;
    signedAt?: string;
    signatoryId?: number;
}

export interface DigitalSignature {
    contractId: number;
    signature: string;
    signatoryId: number;
    timestamp: string;
}

export enum UserStatus {
    PENDING_MODERATION = 'PENDING_MODERATION',
    NEEDS_REVISION = 'NEEDS_REVISION',
    ACTIVE = 'ACTIVE',
    REJECTED = 'REJECTED',
    BLOCKED = 'BLOCKED',
    VERIFIED = 'VERIFIED'
}

export enum UserRole {
    CLIENT = 'CLIENT',
    MODERATOR = 'MODERATOR',
    SIGNATORY = 'SIGNATORY',
    SUPER_ADMIN = 'SUPER_ADMIN'
}

export enum ContractStatus {
    PENDING_SIGNATURE = 'PENDING_SIGNATURE',
    SIGNED = 'SIGNED',
    EXPIRED = 'EXPIRED'
}

export interface User {
    id: number;
    login: string;
    fullName: string;
    email?: string;
    role: UserRole;
    status: UserStatus;
    companyId?: number;
    createdAt: string;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
}

export interface Company {
    id: number;
    bin: string;
    companyName: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    createdAt: string;
    from1C?: boolean; // Флаг для компаний из 1С
}

export interface Document {
    id: number;
    type: DocumentType;
    fileName: string;
    fileUrl: string;
    uploadedAt: string;
}

export enum DocumentType {
    COMPANY_REGISTRATION = 'COMPANY_REGISTRATION',
    TAX_CERTIFICATE = 'TAX_CERTIFICATE',
    BANK_DETAILS = 'BANK_DETAILS',
    DIRECTOR_ID = 'DIRECTOR_ID',
    POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY'
}

export interface PageResponse<T> {
    content: T[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
}

export interface ApiResponse<T = any> {
    success: boolean;
    message: string;
    data?: T;
    error?: string;
}

export interface NotificationMessage {
    type: 'NEW_MODERATION_REQUEST' | 'STATUS_CHANGED' | 'CONTRACT_READY';
    data: any;
    timestamp: string;
}

// Типы для пользователей из 1С
export interface UserFrom1C {
    id: string;
    fullName: string;
    email: string;
    role: 'MODERATOR' | 'SIGNATORY';
    department?: string;
    position?: string;
    isActive: boolean;
}

export interface InviteUserRequest {
    userId: string;
    fullName: string;
    email: string;
    role: UserRole;
}

export interface InviteUserResponse {
    success: boolean;
    message: string;
    inviteToken?: string;
}

export interface InviteToken {
    token: string;
    userId: string;
    email: string;
    role: UserRole;
    expiresAt: string;
    isUsed: boolean;
}

export interface SetPasswordRequest {
    token: string;
    password: string;
    confirmPassword: string;
}

// Типы для клиентов
export interface Client {
    id: number;
    login: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    status: UserStatus;
    createdAt: string;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    company: Company | null;
}

export interface ClientFilters {
    status: string;
    searchTerm: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
}
